if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.custom-select__btn');
      this.listbox = this.querySelector('.custom-select__listbox');
      this.selectedOption = this.querySelector('[aria-selected="true"]');

      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }

      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.options = this.querySelectorAll('.custom-select__option');
      this.nativeSelect = document.getElementById(`${this.id}-native`);
      this.swatches = !!Array.from(this.options).find((el) => 'swatch' in el.dataset);
      this.focusedClass = 'is-focused';
      this.searchString = '';
      this.listboxOpen = false;
      this.selectedOption = this.querySelector('[aria-selected="true"]');
      
      this.buttonWidthSet = false;
      this.cachedListboxHeight = null;
      this.cachedScrollable = false;

      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.button.addEventListener('mousedown', this.handleMousedown.bind(this));
    }

    static getNextVisibleSibling(elem) {
      let sibling = elem.nextElementSibling;
      while (sibling) {
        if (!sibling.hidden && sibling.style.display !== 'none') return sibling;
        sibling = sibling.nextElementSibling;
      }
      return null;
    }

    static getPreviousVisibleSibling(elem) {
      let sibling = elem.previousElementSibling;
      while (sibling) {
        if (!sibling.hidden && sibling.style.display !== 'none') return sibling;
        sibling = sibling.previousElementSibling;
      }
      return null;
    }

    addListboxOpenListeners() {
      this.mouseoverHandler = this.handleMouseover.bind(this);
      this.mouseleaveHandler = this.handleMouseleave.bind(this);
      this.clickHandler = this.handleClick.bind(this);
      this.blurHandler = this.handleBlur.bind(this);

      this.listbox.addEventListener('mouseover', this.mouseoverHandler);
      this.listbox.addEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.addEventListener('click', this.clickHandler);
      this.listbox.addEventListener('blur', this.blurHandler);
    }

    removeListboxOpenListeners() {
      this.listbox.removeEventListener('mouseover', this.mouseoverHandler);
      this.listbox.removeEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.removeEventListener('click', this.clickHandler);
      this.listbox.removeEventListener('blur', this.blurHandler);
    }

    handleKeydown(evt) {
      if (this.listboxOpen) {
        this.handleKeyboardNav(evt);
      } else if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === ' ') {
        evt.preventDefault();
        this.showListbox();
      }
    }

    handleMousedown(evt) {
      if (!this.listboxOpen && evt.button === 0) {
        this.showListbox();
      }
    }

    handleMouseover(evt) {
      if (evt.target.matches('li')) {
        this.focusOption(evt.target);
      }
    }

    handleMouseleave() {
      this.focusOption(this.selectedOption);
    }

    handleClick(evt) {
      if (evt.target.tagName === 'LI') {
        this.selectOption(evt.target);
      }
    }

    handleBlur() {
      if (this.listboxOpen) {
        this.hideListbox();
      }
    }

    handleKeyboardNav(evt) {
      let optionToFocus;

      if (evt.key === 'Tab') {
        evt.preventDefault();
      }

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          if (evt.key === 'ArrowUp') {
            optionToFocus = CustomSelect.getPreviousVisibleSibling(this.focusedOption);
          } else {
            optionToFocus = CustomSelect.getNextVisibleSibling(this.focusedOption);
          }
          if (optionToFocus && !optionToFocus.classList.contains('is-disabled')) {
            this.focusOption(optionToFocus);
          }
          break;
        case 'Enter':
        case ' ':
          evt.preventDefault();
          this.selectOption(this.focusedOption);
          break;
        case 'Escape':
          evt.preventDefault();
          this.hideListbox();
          break;
        default:
          optionToFocus = this.findOption(evt.key);
          if (optionToFocus) {
            this.focusOption(optionToFocus);
          }
          break;
      }
    }

    // =======================================================================
    // ✅ 遵循规范 1：批量读取 → 批量写入
    // ✅ 遵循规范 4：使用 requestAnimationFrame
    // =======================================================================
    setButtonWidth() {
      // 👉 读取阶段（全部先读）
      const buttonStyle = getComputedStyle(this.button);
      const optionStyle = getComputedStyle(this.selectedOption);
      const buttonPadding = parseFloat(buttonStyle.paddingLeft) + parseFloat(buttonStyle.paddingRight);
      const optionPadding = parseFloat(optionStyle.paddingLeft) + parseFloat(optionStyle.paddingRight);
      const buttonBorder = this.button.offsetWidth - this.button.clientWidth;
      const optionWidth = Math.ceil(this.selectedOption.getBoundingClientRect().width);

      // 👉 写入阶段（统一写入）
      requestAnimationFrame(() => {
        this.button.style.setProperty('--custom-select-button-width', 
          `${optionWidth - optionPadding + buttonPadding + buttonBorder}px`);
      });
    }

    showListbox() {
      if (!this.buttonWidthSet) {
        this.setButtonWidth();
        this.buttonWidthSet = true;
      }

      this.listbox.hidden = false;
      this.listboxOpen = true;
      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.listbox.setAttribute('aria-hidden', 'false');

      requestAnimationFrame(() => {
        this.cachedListboxHeight = this.listbox.clientHeight;
        this.cachedScrollable = this.listbox.scrollHeight > this.listbox.clientHeight;
        
        this.focusOption(this.selectedOption);
        this.listbox.focus();
        this.addListboxOpenListeners();
      });
    }

    hideListbox() {
      if (!this.listboxOpen) return;
      this.listbox.hidden = true;
      this.listboxOpen = false;
      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.listbox.setAttribute('aria-hidden', 'true');

      this.cachedListboxHeight = null;
      this.cachedScrollable = false;

      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
        this.focusedOption = null;
      }
      this.button.focus();
      this.removeListboxOpenListeners();
    }

    findOption(key) {
      this.searchString += key;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.searchString = ''; }, 50);

      return Array.from(this.options).find(option => {
        const label = option.innerText.toLowerCase();
        return label.includes(this.searchString) && !option.classList.contains('is-disabled');
      });
    }

    // =======================================================================
    // ✅ 遵循规范 1 + 2：先读后写 + 缓存布局属性
    // =======================================================================
    focusOption(option) {
      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
      }
      this.focusedOption = option;
      this.focusedOption.classList.add(this.focusedClass);

      if (!this.cachedScrollable) return;

      // 👉 读取阶段（全部先读）
      const listboxScrollTop = this.listbox.scrollTop;
      const optionOffsetTop = option.offsetTop;
      const optionOffsetHeight = option.offsetHeight;
      const scrollBottom = this.cachedListboxHeight + listboxScrollTop;
      const optionBottom = optionOffsetTop + optionOffsetHeight;

      // 👉 写入阶段
      requestAnimationFrame(() => {
        if (optionBottom > scrollBottom) {
          this.listbox.scrollTop = optionBottom - this.cachedListboxHeight;
        } else if (optionOffsetTop < listboxScrollTop) {
          this.listbox.scrollTop = optionOffsetTop;
        }
      });
    }

    selectOption(option) {
      if (option !== this.selectedOption) {
        // 👉 读取阶段
        const optionValue = option.dataset.value;
        const optionText = option.firstElementChild.textContent;
        const optionId = option.id;
        const productUrl = option.dataset.productUrl;
        
        let swatchData = null;
        if (this.swatches) {
          swatchData = {
            swatch: option.dataset.swatch || '',
            color: option.style.getPropertyValue('--native-swatch-color'),
            image: option.style.getPropertyValue('--native-swatch-image')
          };
        }

        // 👉 写入阶段
        requestAnimationFrame(() => {
          option.setAttribute('aria-selected', 'true');
          this.selectedOption.setAttribute('aria-selected', 'false');

          if (swatchData) {
            this.button.dataset.swatch = swatchData.swatch;
            if (swatchData.color) {
              this.button.style.setProperty('--native-swatch-color', swatchData.color);
              this.button.style.setProperty('--native-swatch-image', '');
            }
            if (swatchData.image) {
              this.button.style.setProperty('--native-swatch-image', swatchData.image);
              this.button.style.setProperty('--native-swatch-color', '');
            }
          }

          this.button.firstElementChild.textContent = optionText;
          this.listbox.setAttribute('aria-activedescendant', optionId);
          this.selectedOption = document.getElementById(optionId);

          if (this.nativeSelect) {
            this.nativeSelect.value = optionValue;
            this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            this.dispatchEvent(new CustomEvent('change', {
              bubbles: true,
              detail: { selectedValue: optionValue, productUrl: productUrl }
            }));
          }
        });
      }
      this.hideListbox();
    }
  }

  customElements.define('custom-select', CustomSelect);
}